import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Paper,
  Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CodeIcon from '@mui/icons-material/Code';

interface ParsingConfig {
  enabled: boolean;
  question_delimiter: string;
  answer_delimiter?: string;
  delimiter_type: 'prefix' | 'full_line' | 'regex';
  number_pattern: string;
  case_sensitive: boolean;
  include_delimiter_in_content: boolean;
}

interface ParsingConfigEditorProps {
  value: ParsingConfig;
  onChange: (config: ParsingConfig) => void;
}

const ParsingConfigEditor: React.FC<ParsingConfigEditorProps> = ({ value, onChange }) => {
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState<any>(null);

  const handleChange = (field: keyof ParsingConfig, newValue: any) => {
    onChange({
      ...value,
      [field]: newValue
    });
  };

  const examplesByType = {
    prefix: {
      description: 'Matches when delimiter appears at start of content',
      examples: [
        'QUESTION 1: What is...?',
        'Question 2: Explain...',
        'Q3: Define...'
      ]
    },
    full_line: {
      description: 'Matches when entire line contains only the delimiter',
      examples: [
        '=== QUESTION 1 ===',
        'QUESTION 2',
        '--- Q3 ---'
      ]
    },
    regex: {
      description: 'Custom regular expression pattern',
      examples: [
        'Pattern: (\\d+)\\. matches "1. ", "2. "',
        'Pattern: Question (\\d+): matches "Question 1:"',
        'Pattern: ^Q(\\d+) matches "Q1", "Q2"'
      ]
    }
  };

  const presetConfigs = [
    {
      name: 'Standard Questions',
      config: {
        enabled: true,
        question_delimiter: 'QUESTION',
        answer_delimiter: undefined,
        delimiter_type: 'prefix' as const,
        number_pattern: '\\d+',
        case_sensitive: false,
        include_delimiter_in_content: false
      }
    },
    {
      name: 'Question/Answer Format',
      config: {
        enabled: true,
        question_delimiter: 'QUESTION',
        answer_delimiter: 'ANSWER',
        delimiter_type: 'prefix' as const,
        number_pattern: '\\d+',
        case_sensitive: false,
        include_delimiter_in_content: false
      }
    },
    {
      name: 'Numbered List (1., 2., 3.)',
      config: {
        enabled: true,
        question_delimiter: '(\\d+)\\.',
        answer_delimiter: undefined,
        delimiter_type: 'regex' as const,
        number_pattern: '\\d+',
        case_sensitive: false,
        include_delimiter_in_content: false
      }
    }
  ];

  const applyPreset = (preset: typeof presetConfigs[0]) => {
    onChange(preset.config);
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="div">
            Submission Parsing Configuration
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={value.enabled}
                onChange={(e) => handleChange('enabled', e.target.checked)}
                color="primary"
              />
            }
            label={value.enabled ? 'Enabled' : 'Disabled'}
          />
        </Box>

        {value.enabled && (
          <>
            {/* Preset Configurations */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Quick Presets</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {presetConfigs.map((preset, idx) => (
                    <Chip
                      key={idx}
                      label={preset.name}
                      onClick={() => applyPreset(preset)}
                      color="primary"
                      variant="outlined"
                      clickable
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Delimiter Type */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Delimiter Type</InputLabel>
              <Select
                value={value.delimiter_type}
                onChange={(e) => handleChange('delimiter_type', e.target.value)}
                label="Delimiter Type"
              >
                <MenuItem value="prefix">Prefix Match</MenuItem>
                <MenuItem value="full_line">Full Line Match</MenuItem>
                <MenuItem value="regex">Custom Regex</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info" icon={<HelpOutlineIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                {examplesByType[value.delimiter_type].description}
              </Typography>
              {examplesByType[value.delimiter_type].examples.map((ex, idx) => (
                <Typography key={idx} variant="caption" display="block">
                  • {ex}
                </Typography>
              ))}
            </Alert>

            {/* Question Delimiter */}
            <TextField
              fullWidth
              margin="normal"
              label={value.delimiter_type === 'regex' ? 'Question Pattern (Regex)' : 'Question Delimiter'}
              value={value.question_delimiter}
              onChange={(e) => handleChange('question_delimiter', e.target.value)}
              placeholder="QUESTION"
              helperText={
                value.delimiter_type === 'regex'
                  ? 'Use capturing group () for question number. Example: QUESTION (\\d+)'
                  : 'Text that marks the start of a question'
              }
            />

            {/* Answer Delimiter */}
            <TextField
              fullWidth
              margin="normal"
              label="Answer Delimiter (Optional)"
              value={value.answer_delimiter || ''}
              onChange={(e) => handleChange('answer_delimiter', e.target.value || undefined)}
              placeholder="ANSWER"
              helperText="If empty, everything after question delimiter is treated as the answer"
            />

            {/* Number Pattern */}
            {value.delimiter_type !== 'regex' && (
              <TextField
                fullWidth
                margin="normal"
                label="Number Pattern (Regex)"
                value={value.number_pattern}
                onChange={(e) => handleChange('number_pattern', e.target.value)}
                placeholder="\d+"
                helperText="Pattern to extract question number. Default: \d+ (any digits)"
              />
            )}

            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={value.case_sensitive}
                    onChange={(e) => handleChange('case_sensitive', e.target.checked)}
                  />
                }
                label="Case Sensitive"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={value.include_delimiter_in_content}
                    onChange={(e) => handleChange('include_delimiter_in_content', e.target.checked)}
                  />
                }
                label="Include Delimiter in Content"
              />
            </Box>

            {/* Test Section */}
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CodeIcon />
                  <Typography>Test Parser</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Test Text"
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  placeholder="Paste sample submission text here to test parsing..."
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="contained"
                  onClick={async () => {
                    try {
                      const response = await fetch('/api/rubrics/test-parser/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          text: testText,
                          config: value
                        })
                      });
                      const result = await response.json();
                      setTestResult(result);
                    } catch (error) {
                      console.error('Test failed:', error);
                    }
                  }}
                  disabled={!testText}
                >
                  Test Parse
                </Button>

                {testResult && (
                  <Paper sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Result: {testResult.parsed ? '✅ Success' : '❌ Failed'}
                    </Typography>
                    {testResult.warnings?.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        {testResult.warnings.map((w: string, idx: number) => (
                          <Typography key={idx} variant="caption" display="block">
                            {w}
                          </Typography>
                        ))}
                      </Alert>
                    )}
                    {testResult.parsed && (
                      <>
                        <Typography variant="caption" display="block" gutterBottom>
                          Found {testResult.questions?.length || 0} question(s)
                        </Typography>
                        {testResult.questions?.map((q: any, idx: number) => (
                          <Box key={idx} sx={{ mt: 1, p: 1, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" fontWeight="bold">
                              Question {q.number}
                            </Typography>
                            {q.question_text && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Q: {q.question_text.substring(0, 100)}...
                              </Typography>
                            )}
                            <Typography variant="caption" display="block" color="primary">
                              A: {q.answer_text.substring(0, 100)}...
                            </Typography>
                          </Box>
                        ))}
                      </>
                    )}
                  </Paper>
                )}
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ParsingConfigEditor;
